<template>
  <sp-menu
    class="finder-landing-location"
    :open="open"
    close-on-content-click="false"
    pointer
    @update-open="handleOpen"
  >
    <sp-text-field
      ref="activator"
      slot="activator"
      v-preserve-properties:messages="locationValidationMessages"
      class="doctor-finder-landing-search__location-input"
      :data-testid="TestId.finder.nearFilter"
      :value.prop="displayText"
      :placeholder="locationPlaceholder"
      readonly
      :required="settings.requireLocation"
      :custom-validity="landingText('location_invalid')"
      prepend-icon="map-pin-filled"
      append-icon="popup-arrow-filled"
      propagate-append-click
      variant="plain"
    ></sp-text-field>
    <div class="search-location-dropdown">
      <div class="search-panel form-group search-location__panel">
        <sp-number-slider
          v-if="sliderControl"
          v-preserve-properties:values="sliderStops"
          v-preserve-properties:format-value="formatDistance"
          v-preserve-properties:min="sliderMin"
          v-preserve-properties:max="sliderMax"
          show-bounds
          :value.prop="selectedDistance"
          :label="t('distance_maximum')"
          :input-test-id="TestId.finder.distanceSliderInput"
          @keydown.up.down.home.end.stop
          @change="handleSliderChange"
        ></sp-number-slider>
        <template v-else>
          <label class="distance filter-group-label">{{ t("distance_label") }}</label>
          <sp-select
            v-preserve-properties:items="distanceOptions"
            :value.prop="pickedDistance"
            :prompt="anyDistancePrompt"
            :prepend-selected-with="t('distance_within')"
            :aria-label="t('distance_label')"
            :data-testid="TestId.finder.landingDistance"
            :item-test-id="TestId.finder.landingDistanceOption"
            selected-icon="check-filled"
            no-sheet
            no-caret-flip
            caret-variant="image"
            pointer
            no-restore-focus
            @change="handleDistanceChange"
          ></sp-select>
        </template>
        <div v-if="geolocationSupported" class="geocode-location-panel">
          <sp-button
            variant="appearance-link"
            class="finder-landing-location__geolocate"
            :data-testid="TestId.finder.nearCurrentLocation"
            :disabled="isRetrievingCurrentLocation"
            @click="handleGeolocate"
          >
            {{ geolocateText }}
          </sp-button>
        </div>
        <label class="from filter-group-label" :for="queryFieldId">{{ landingText("from") }}</label>
        <SpGoogleMapsAutoComplete
          class="finder-landing-location__autocomplete"
          :sort-order="placeSortOrder"
          :search-query="landingSearchQuery"
          :placeholder="landingText('search_location')"
          :input-test-id="TestId.finder.distanceFilterInput"
          @update-model-value="updateGoogleResult"
          @update-search-query="handleQuery"
        >
          <template #input="{ query, setQuery, disabled, placeholder, inputTestId }">
            <sp-text-field
              :id="queryFieldId"
              class="finder-landing-location__query"
              density="compact"
              :value.prop="query"
              :disabled="disabled"
              :placeholder="placeholder"
              :input-test-id="inputTestId"
              @input.stop="handleQueryInput($event, setQuery)"
              @change.stop
              @keydown.space.stop
              @keydown.enter.stop.prevent
            ></sp-text-field>
          </template>
        </SpGoogleMapsAutoComplete>
      </div>
      <div class="list-wrapper">
        <sp-quantum-select
          v-preserve-properties:items="landingLocations"
          :style="locationListStyle"
          :aria-label="landingText('location')"
          no-filter
          no-menu
          appearance="inline"
          hide-title-bar
          hide-search-bar
          selected-prepend-icon=""
          :selected-icon-position="selectedIconPosition"
          :listen-on-key-event="open"
          :loading="loading"
          :item-test-id="TestId.finder.distanceFilterOption"
          item-title="name"
          @update-selected="handleSelect"
        ></sp-quantum-select>
      </div>
    </div>
  </sp-menu>
</template>

<script setup>
import { computed, nextTick, onMounted, onScopeDispose, reactive, ref, useId, useTemplateRef, watch } from "vue";
import { useDistanceFilter } from "#ce/shared/filters/distance/composables/useDistanceFilter";
import SpGoogleMapsAutoComplete from "#ce/components/SpGoogleMaps/SpGoogleMapsAutoComplete.ce.vue";
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { t } from "#ce/shared/filters/i18n";
import { i18n } from "#ce/components/SpFinder/i18n";
import { TestId } from "#/test-ids";
import { SelectedIconPosition } from "#ce/components/SpList/props/list";

const queryFieldId = `finder-landing-location-query-${useId()}`;

// Milliseconds an unanswered device location request waits before the IP fallback runs.
const automaticLocationTimeout = 10_000;

const props = defineProps({
  settings: { type: Object, required: true },
  draft: { type: Object, required: true },
  active: Boolean,
  transientLocation: Boolean,
  locationPins: Boolean,
  locationPinIcon: { type: String, required: true },
});
const emit = defineEmits(["update-location"]);
const open = ref(false);
const activator = useTemplateRef("activator");
const validationRequested = ref(false);
const locationPlaceholder = computed(() =>
  landingText(props.settings.requireLocation ? "required_location" : "location"),
);
const distance = props.settings.filterOptions?.distance ?? {};
const filterProps = reactive({
  locationParam: "zip",
  near: props.draft.zip,
  lat: props.draft.lat,
  lng: props.draft.lng,
  distance: landingDistance(props.draft.distance ?? distance.default),
  distances: distance.values,
  distanceControl: distance.control,
  distanceMin: distance.min,
  distanceMax: distance.max,
  allowAny: distance.allow_any !== false,
  anyDistanceValue: "any",
  preserveDistanceOnClear: true,
  submitCurrentLocation: true,
  coordinatePrecision: 3,
  commonPlaces: props.settings.commonPlaces,
  url: props.settings.url,
  geocodeUrl: props.settings.geocodeUrl,
  nonClearable: Boolean(props.settings.filterOptions?.required_filters_modal),
  locationsGroupLabel: computed(() => landingText("common_locations")),
  currentLocationDisplayText: computed(() => landingText("geolocated")),
});
const location = useDistanceFilter(filterProps);
const {
  displayText: defaultDisplayText,
  selectedLocation,
  distanceOptions,
  pickedDistance,
  anyDistancePrompt,
  selectedDistance,
  sliderControl,
  distanceRange,
  distances,
  geolocationSupported,
  isRetrievingCurrentLocation,
  placeSortOrder,
  searchQuery,
  locations,
  loading,
  updateGoogleResult,
  transientLocation: selectedLocationIsTransient,
} = location;
const geolocateText = computed(() => landingText(isRetrievingCurrentLocation.value ? "geolocating" : "geolocate"));
const pinnedListStyle = Object.freeze({
  "--sp-comp-quantum-select-item-icon-fill":
    "var(--sp-comp-finder-landing-location-list-icon-fill, var(--sp-sys-color-neutral))",
  "--sp-comp-quantum-select-item-icon-size":
    "var(--sp-comp-finder-landing-location-list-icon-size, var(--sp-sys-icon-size-medium))",
});
const locationListStyle = computed(() => (props.locationPins ? pinnedListStyle : undefined));
const selectedIconPosition = computed(() =>
  props.locationPins ? SelectedIconPosition.END : SelectedIconPosition.START,
);
const sliderStops = computed(() => (distanceRange.value ? undefined : distances.value));
const sliderMin = computed(() => distanceRange.value?.min);
const sliderMax = computed(() => distanceRange.value?.max);
const landingLocations = computed(() =>
  locations.value.map((place) => {
    const presentation = { ...place.props };
    if (props.locationPins) {
      presentation.prependIcon = props.locationPinIcon;
    } else {
      delete presentation.prependIcon;
    }
    delete presentation.prependIconProps;
    return { ...place, props: presentation };
  }),
);
const locationMissing = computed(() => {
  const values = location.model.value;
  return props.settings.requireLocation && !values.get("zip") && !(values.has("lat") && values.has("lng"));
});
const locationValidationMessages = computed(() =>
  validationRequested.value && locationMissing.value ? [landingText("location_invalid")] : [],
);
function resetValidation() {
  validationRequested.value = false;
}
function reportValidity() {
  validationRequested.value = true;
  // A readonly input skips required validation, so the selected location is checked directly.
  if (locationMissing.value) {
    activator.value?.inputElement?.focus();
    return false;
  }
  return true;
}
let geolocationPending = false;
const initialized = ref(false);
let automaticGeneration = 0;
let explicitlySelected = false;
const landingSearchQuery = computed(() =>
  selectedLocation.value?.value === props.draft.zip && searchQuery.value === selectedLocation.value?.name
    ? props.draft.zip
    : searchQuery.value,
);
const selectedPlaceLabel = computed(() => {
  const selected = selectedLocation.value;
  const savedLabel = selected?.value === props.draft.zip ? props.draft.locationDisplayName : undefined;
  return selected?.selectedText || savedLabel || selected?.name;
});
const displayText = computed(() => {
  if (!selectedLocation.value || selectedPlaceLabel.value === selectedLocation.value.name) {
    return defaultDisplayText.value;
  }
  const miles = Number.parseInt(selectedDistance.value);
  return Number.isFinite(miles)
    ? t("distance_summary", { distance: t("distance_miles", { count: miles }), location: selectedPlaceLabel.value })
    : selectedPlaceLabel.value;
});

function formatDistance(miles) {
  return t("distance_miles", { count: miles });
}

function landingDistance(value) {
  if (distance.control === "slider" && !Number.isFinite(Number.parseInt(value)) && distance.values?.length) {
    return Math.max(...distance.values);
  }
  return value;
}

async function restoreDraft(draft) {
  automaticGeneration += 1;
  await location.setFilterValues({ ...draft, distance: landingDistance(draft.distance ?? distance.default) });
  restoreLocationProvenance();
}

function restoreLocationProvenance() {
  selectedLocationIsTransient.value = props.transientLocation;
  const hasCoordinates =
    props.draft.lat != null && props.draft.lat !== "" && props.draft.lng != null && props.draft.lng !== "";
  explicitlySelected = (Boolean(props.draft.zip) || hasCoordinates) && !props.transientLocation;
}

function landingText(key) {
  return i18n.global.t(`landing.${key}`);
}
function handleOpen({ detail }) {
  open.value = Boolean(detail?.[0]);
}
async function reportLocation() {
  await nextTick();
  const values = Object.fromEntries(location.model.value);
  emit(
    "update-location",
    {
      zip: values.zip ?? null,
      lat: values.lat ?? null,
      lng: values.lng ?? null,
      distance: selectedDistance.value,
      locationDisplayName: selectedPlaceLabel.value ?? null,
    },
    { explicitlySelected, transientLocation: selectedLocationIsTransient.value },
  );
}
function handleSelect({ detail }) {
  automaticGeneration += 1;
  const value = detail?.[0]?.[0];
  if (!value && filterProps.nonClearable) {
    return;
  }
  explicitlySelected = Boolean(value);
  location.updateSelectedLocation(value);
  open.value = false;
  reportLocation();
}
function handleDistanceChange(event) {
  location.selectDistance(event.target.value);
  reportLocation();
}
function handleSliderChange({ detail }) {
  location.selectDistance(detail?.[0]);
  reportLocation();
}
function handleQueryInput(event, setQuery) {
  setQuery(event.target.value);
}
function handleQuery(query) {
  automaticGeneration += 1;
  location.cancelLocationLookup();
  const hadSelection = Boolean(selectedLocation.value);
  location.handleSearchInput(query);
  if (!query && hadSelection && !selectedLocation.value) {
    explicitlySelected = false;
    reportLocation();
  }
}
function handleGeolocate() {
  automaticGeneration += 1;
  geolocationPending = true;
  location.selectCurrentLocation();
}
function finishGeolocation(retrieving) {
  if (!retrieving && geolocationPending) {
    geolocationPending = false;
    if (selectedLocationIsTransient.value) {
      explicitlySelected = false;
      reportLocation();
    }
  }
}
async function initializeLocation() {
  await location.initialize();
  restoreLocationProvenance();
  initialized.value = true;
}
async function initializeAutomaticLocation() {
  const generation = ++automaticGeneration;
  if (!props.active || !initialized.value) {
    location.cancelLocationLookup();
    return;
  }
  if (selectedLocation.value || searchQuery.value || isRetrievingCurrentLocation.value) {
    return;
  }
  explicitlySelected = false;
  let selected = false;
  if (props.settings.askDeviceForLocation && geolocationSupported.value) {
    selected = await location.selectCurrentLocation({ notify: false, timeout: automaticLocationTimeout });
  }
  if (generation !== automaticGeneration || !props.active) {
    return;
  }
  if (!selected && !selectedLocation.value && props.settings.geocodeIP) {
    selected = await location.selectRemoteLocation();
  }
  if (generation === automaticGeneration && props.active && selected) {
    reportLocation();
  }
}
watch(isRetrievingCurrentLocation, finishGeolocation, { flush: "post" });
watch(() => props.draft, restoreDraft);
watch(() => props.transientLocation, restoreLocationProvenance);
watch([() => props.active, initialized], initializeAutomaticLocation);
onMounted(initializeLocation);
function disposeLocation() {
  automaticGeneration += 1;
  geolocationPending = false;
  filterProps.disabled = true;
  location.clear();
}
onScopeDispose(disposeLocation);
defineExpose({
  reportValidity,
  resetValidation,
  clearQuery: () => location.handleSearchInput(""),
});
</script>
